import { useContext } from 'react'
import { errorMessage, moduleLabel, t } from '@ctcells/i18n'

import { cx } from '../../atoms/cx.ts'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { ChamberView } from '../../figures/chamber3d/ChamberView.tsx'
import type { ChamberPhase } from '../../figures/chamber3d/chamberScene.ts'

import { ChamberActivityContext } from './chamberActivity.ts'
import { chamberNote, figureProgress } from './bundleCardModel.ts'
import { chamberFill, chamberFillText, type ChamberStageNote } from './flowVocabulary.ts'
import { trackingActiveWord } from './trackingProgress.ts'
import type { SampleBundle } from './patientChambers.ts'

/**
 * **챔버 한 개 — 그림 + 웰 이름 + 아래 한 마디.** 시료 카드(`SampleBundles`)와 모니터링 시료
 * 서랍(`MonitoringSampleDrawer`)이 **같은 컴포넌트**를 쓴다(2026-09-28 여기로 뺐다).
 *
 * 🔴 **말하는 것은 「담김 / 빈 / 실패 / 끝남」 넷뿐이다**(eric 지시 2026-09-22 — 같은 말을
 *    세 번 하지 마라). `chamberFill()` 이 그 줄이기를 한다.
 * 🔴 **시료 점(`●`)을 그리지 않는다.** 챔버 안에 시료를 콕 찍으면 「그 챔버에 시료가 몇 개
 *    들었나」를 아는 척하게 된다 — 서버에 그 축이 없다.
 * 🔴 그림 자체는 `aria-hidden` 이라 한 챔버가 두 번 읽히지 않는다.
 * ⚠ 크기는 부르는 쪽 CSS 가 정한다(`--chamber-w`) — 카드는 작게, 서랍은 크게.
 */
export function BundleChamber({
  dot,
  phase,
  note,
  vacancy,
  working = false,
}: {
  dot: SampleBundle['chambers'][number]
  /**
   * **지금 작업 중인 챔버인가**(2026-10-01 회의 「9개 중 현재 작업 중인 칩이 구분되어야 함」). 판정은 부르는 쪽이
   * `chamberWorking()`(`bundleCardModel.ts`)으로 한다 — 이 챔버 한 마디가 「진행」일 때와 같은 값이다.
   * 참이면 강조 테 + 옅은 강조 면이 선다. 🔴 색만으로 말하지 않는다 — 아래 한 마디 배지 「진행」이 같은 사실을 글자로 말한다.
   * 안 넘기면 꺼짐(서랍 등 종전 자리는 그대로).
   */
  working?: boolean | undefined
  /** 시료가 없는 묶음의 까닭(`bundleCardModel().vacancy`). 있으면 단계 대신 이 낱말이 챔버 아래에 선다 */
  vacancy?: string | undefined
  /**
   * 🔴 **우물 안이 공정 단계대로 바뀐다**(eric 2026-09-23). 출처는 배치 표지이고, 배치와
   *    다른 시료(폐기·실패)는 «그 시료가 멈춘 자리»로 언다 — `bundleCardModel` 이 정해 넘긴다.
   */
  phase?: ChamberPhase | undefined
  /**
   * **「지금 어디까지」 한 마디** — 모듈 칩 하나 + 상태 낱말 하나(eric 2026-09-23 「최종만 적으라고」).
   * 🔴 **낱말은 준비 중 · 진행 · 완료 셋뿐**이다(폐기·실패는 예외로 남는다).
   */
  note?: ChamberStageNote | undefined
}) {
  const reason =
    dot.errorCode === undefined ? undefined : errorMessage({ error_code: dot.errorCode })
  const state = chamberFill(dot)
  /*
   * 🔴 **배정을 모르는 챔버는 단계를 말하지 않는다.** 누구 것인지 모르는 우물에 「염색 진행 중」을
   *    적으면 **그 챔버가 이 배치를 돌고 있다**는, 우리가 모르는 사실이 된다(BR-31 과 같은 결).
   */
  /* 🔴 챔버마다 갈리는 말은 모델이 정한다(`chamberNote` — 서버가 그 웰을 가리킬 때만) */
  const shown = state === 'unassigned' ? undefined : chamberNote(note, dot.progress)
  const activity = useContext(ChamberActivityContext)
  /* 🔴 서버가 이 챔버를 가리켜 「진행」이라 할 때만(트레이 뷰 · 분주 기록) — 모듈 근거로 아홉 칸에 같이 달지 않는다 */
  /*
   * ⚠ 서버 추적이 그 웰의 «지금 하는 일»을 이미 말했으면(원심 대기 · 완료 + `active` → 적재 중 · 회수 중 · `trackingActiveWord`) 모듈 상태 한 줄을
   *   더 달지 않는다 — 웰은 「회수 중」, 모듈은 「실행 중」으로 한 챔버가 두 말을 했다(2026-10-23).
   */
  const nowText =
    dot.progress !== undefined &&
    dot.progress.basis !== 'module' &&
    dot.progress.status === 'active' &&
    !(dot.progress.basis === 'tracking' && trackingActiveWord(dot.progress.track) !== undefined)
      ? activity[dot.progress.stage]
      : undefined
  /*
   * 🔴 **그림의 단계는 그 챔버의 단계다**(eric 2026-10-08 「시료 쪽에 왜 어셈블러로 안 넘어가? 지금 진행 중인데?」). 서버가 챔버를 가리켜
   *    말하면(트레이 뷰 `tray` · 분주 기록 `well`) 그 챔버의 단계 · 완료 여부로 그린다 — 어셈블러가 A2 를 누르는 동안 아직 차례가 안 온
   *    챔버는 「원심 완료」(세포층)로, 끝난 A1 은 「어셈블 완료」(젤 막)로 선다. 종전에는 카드(배치) 단계 하나로 아홉 칸을 같이 그려
   *    아래 글자는 「원심분리기 완료」인데 그림은 TC 를 누르고 있었다. 모듈 근거(`module`)는 종전대로 카드 단계다
   */
  const ownPhase: ChamberPhase | undefined =
    dot.progress !== undefined && dot.progress.basis !== 'module'
      ? {
          stage: dot.progress.stage,
          /*
           * 🔴 서버 추적(`tracking`)의 `settled` 는 «그 단계 안 · 지금 그 웰은 아님»(원심 중 · UV 중 · 염색 k/N)이라 완료 그림이 아니다
           *    (`trackingProgress.ts`) — 완료로 그리면 「원심 중」 챔버에 세포층이 앉는다. 트레이 뷰 · 분주 근거의 `settled` 는 종전대로다
           */
          done:
            dot.progress.status === 'done' ||
            (dot.progress.status === 'settled' && dot.progress.basis !== 'tracking'),
        }
      : phase
  return (
    <span
      className={cx('chamber', `chamber--${state}`, working && 'chamber--working')}
      title={reason ?? undefined}
      /* 챔버별 진행 어휘(`ChamberProgressStatus`) — 3D 단계 표현 · 시험이 읽는 자리. 모르면 속성이 없다 */
      data-progress={dot.progress?.status}
      data-progress-basis={dot.progress?.basis}
      data-working={working ? 'true' : undefined}
    >
      <ChamberView
        state={state}
        well={dot.well}
        {...(ownPhase === undefined ? {} : { phase: ownPhase })}
        /* 챔버 진행 4종 — 통합 콘솔 2D 의 테가 읽는다(준비 중 점선 · 진행 굵은 테 · 완료 실선 · 실패 해칭) */
        /* 🔴 모듈 근거의 「진행」은 `stage`(그 단계에 와 있음 — 굵은 테 · 도구 없음)로 넘긴다(`figureProgress` · eric 2026-10-06) */
        {...(dot.progress === undefined ? {} : { progress: figureProgress(dot.progress) })}
        /* 🔴 웰 근거일 때만 — 모듈 근거를 «챔버마다 다른 사실»처럼 3D 에 넘기지 않는다(스포이드 자리 · `chamberTool`) */
        {...(dot.progress?.basis === 'well' &&
        dot.progress.status !== 'transfer' &&
        dot.progress.status !== 'settled'
          ? {
              wellProgress: {
                status: dot.progress.status,
                ...(dot.progress.latest === true ? { latest: true } : {}),
              },
            }
          : {})}
      />
      <span className="a-sr">
        {t('monitoring.flow.chamber', { well: dot.well })} {vacancy ?? chamberFillText(dot)}
        {reason === undefined ? '' : ` ${reason}`}
      </span>
      {/*
        🔴 **웰 이름 줄 «아래»의 작은 글자 한 줄**. 편차일 때만 글리프가 선다 — 색«만»으로
           가르지 않는 규약이라 글자가 늘 같이 말한다.
      */}
      {vacancy !== undefined ? (
        <VacancyNote word={vacancy} />
      ) : shown === undefined ? null : (
        <ChamberNoteLine note={shown} />
      )}
      {/* 진행 중인 챔버 — 그 단계 모듈이 지금 하는 일(eric 2026-10-08 「진행 중인 내용도 보여줘」) */}
      {nowText === undefined ? null : (
        <span className="chamber__now" data-part="chamber-now">
          {nowText}
        </span>
      )}
    </span>
  )
}

/**
 * 한 마디 → 상태 배지의 «원값»(`StatusBadge.status` · 종류만 고른다 — 글자는 `note.word` 그대로다).
 *
 * 🔴 **새로 추론하지 않는다** — 낱말을 정한 자리가 같이 넘긴 축(`status` · `tone`)을 배지 종류로 옮길 뿐이다.
 *   준비 중 · 대기(뒤처짐) = 대기 꼴 · 진행 = 진행 중 꼴 · 완료 = 완료 꼴 · 실패 · 폐기 = 위험 꼴.
 * ⚠ 원값 낱말은 `statusKind` 사전의 것을 빌린다(모듈 `IDLE` · 작업 `running` · `completed` · 판정 `fail` · 시료 `discarded`).
 */
const NOTE_BADGE = {
  ready: 'IDLE',
  doing: 'running',
  done: 'completed',
  stage: 'IDLE',
  /* 이송 중 — 중립 꼴(진행 칠을 하지 않는다 · 팀장 (가)안 2026-10-06) */
  transfer: 'IDLE',
} as const
const TONE_BADGE = { fail: 'fail', halted: 'discarded', behind: 'IDLE' } as const

function noteBadgeStatus(note: ChamberStageNote): string {
  if (note.tone !== undefined) return TONE_BADGE[note.tone]
  return note.status === undefined ? 'IDLE' : NOTE_BADGE[note.status]
}

/**
 * **챔버 아래 한 마디** — `염색기 (진행)` — 공정명 글자 + **상태 배지**(디자인팀 시안 2026-10-01 §3
 * 「"분주 완료" 글자 → 공정명 + 상태 배지(완료 · 진행 중 · 대기 · 준비 중) · 상태를 색과 글자로 함께 구분」).
 *
 * 🔴 **한 자리에서만 그린다** — 챔버 아래와 「챔버 배정 없음」 옆이 같은 모양이어야 한다.
 * 🔴 **공정명 + 배지는 안 갈라진다**(`nowrap`). 챔버 폭이 좁아도 둘이 한 덩어리로 움직인다.
 * 🔴 배지 글자는 종전 낱말 그대로다(준비 중 · 진행 · 완료 · 실패 · 폐기 · 대기 — `chamberStageNote` · `chamberNote`).
 *    배지는 공용 `StatusBadge` — 콘솔 스킨에서는 정상(진행 · 완료 · 대기)이 글자만, 실패 · 폐기만 표지가 선다.
 * ⚠ 공정명은 **전체 이름**이다(「원심」 아니라 「원심분리기」 · eric 2026-09-23). 종전에는 이 이름이 채움 알약(칩)이었는데
 *   상태가 알약이 되면서 **글자로 물러났다**(시안 `.ch__mod`) — 알약 둘이 나란히 서면 어느 쪽이 상태인지 흐려진다.
 */
export function ChamberNoteLine({ note }: { note: ChamberStageNote }) {
  return (
    <span className="chamber__note" title={note.title}>
      <span className="chamber__pair">
        {/* 칩 이름이 빈 글자면 배지 하나로 선다(「분주 중」 · `chamberNote`) */}
        {note.label === '' ? null : (
          <span className="chamber__mod">{note.label ?? moduleLabel(note.device)}</span>
        )}
        {/*
          🔴 「그 단계에 와 있음」(`stage`)에는 배지가 없다 — 공정명만 선다(eric 2026-10-06 · `chamberNote`).
             「진행」 배지를 아홉 칸에 같이 세우면 안 도는 챔버까지 진행이라고 말한다. 진행 중은 묶음 위 한 줄이 말한다.
        */}
        {note.status === 'stage' && note.tone === undefined ? null : (
          <span
            className={cx(
              'chamber__word',
              note.tone !== undefined && `chamber__word--${note.tone}`,
              /*
               * 완료는 테마색 · 진행은 초록(eric 2026-10-12 「완료 칩을 테마색으로 하고.. 실행 중은.. 초록색으로」 — 10-08 의 「완료 = 초록」을 뒤집었다).
               * 편차가 있으면 편차 색이 먼저다
               */
              note.tone === undefined && note.status === 'done' && 'chamber__word--done',
              note.tone === undefined && note.status === 'doing' && 'chamber__word--doing',
            )}
          >
            <StatusBadge label={note.word} status={noteBadgeStatus(note)} />
          </span>
        )}
      </span>
    </span>
  )
}

/**
 * **시료 없는 챔버 아래 한 마디** — 모듈 칩 없이 흐린 낱말 하나(`.chamber__word--behind` 결).
 * 🔴 칩을 세우지 않는다 — 누구 것인지 모르는 챔버에 「원심분리기」를 달면 그 챔버가 이 배치를
 *    돈다는, 우리가 모르는 사실이 된다(`BundleChamber` 의 `shown` 규칙과 같은 결).
 */
export function VacancyNote({ word }: { word: string }) {
  return (
    <span className="chamber__note">
      <span className="chamber__pair">
        <span className="chamber__word chamber__word--behind">{word}</span>
      </span>
    </span>
  )
}
